'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { GA_EVENTS, GA_PARAMS } from '@/constants/analytics';
import { trackEvent } from '@/lib/analytics/trackEvent';
import {
  dirForLocale,
  localeDisplayNames,
  localeHreflang,
  localizedHref,
  locales,
  pathWithoutLocale,
  type Locale,
} from '@/lib/i18n';

import styles from './LanguageSwitcher.module.css';

type LanguageSwitcherProps = {
  locale: Locale;
  /** Accessible name for the language nav (from server messages). */
  languageSwitcherLabel: string;
  /** Footer uses Figma Label S uppercase + bottom border (mobile/desktop footer). */
  variant?: 'default' | 'footer';
};

export function LanguageSwitcher({
  locale: currentLocale,
  languageSwitcherLabel,
  variant = 'default',
}: LanguageSwitcherProps) {
  const pathname = usePathname() ?? '/';
  const path = pathWithoutLocale(pathname);
  const isFooter = variant === 'footer';

  return (
    <nav
      className={isFooter ? styles.navFooter : styles.nav}
      aria-label={languageSwitcherLabel}
    >
      <ul className={isFooter ? styles.listFooter : styles.list}>
        {locales.map((locale) => (
          <li key={locale}>
            <Link
              className={isFooter ? styles.linkFooter : styles.link}
              href={localizedHref(locale, path)}
              hrefLang={localeHreflang(locale)}
              lang={locale}
              dir={dirForLocale(locale)}
              aria-current={locale === currentLocale ? 'page' : undefined}
              onClick={() => {
                if (locale === currentLocale) return;
                trackEvent(GA_EVENTS.languageSwitch, {
                  [GA_PARAMS.fromLocale]: currentLocale,
                  [GA_PARAMS.toLocale]: locale,
                });
              }}
            >
              {localeDisplayNames[locale]}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
